import { useTranslation } from "react-i18next";

import {
  getSummaryTableColumns,
  loanSpecialTableColumns,
  loanRegularTableColumns,
  totalOverviewTableColumns,
  expectedIncomeTableColumns,
  getEstimatedTaxResultsTableColumns,
} from "./DetailedView.config";

import {
  expectedIncomeTableBottomText,
  estimatedTaxResultsTableBottomText,
} from "./DetailedView.config";

import { Typography } from "shared/ui/components/Typography";

import { InvestmentBasicData } from "components/InvestmentBasicData";

import { InfoCard } from "./components/InfoCard";
import { PriceCard } from "./components/PriceCard";
import { LoansCard } from "./components/LoansCard";
import { ClientCard } from "./components/ClientCard";
import { PenaltyCard } from "./components/PenaltyCard";
import { TimelineCard } from "./components/TimelineCard";
import { DetailedTable } from "./components/DetailedTable";
import { FinancingCard } from "./components/FinancingCard";
import { CalculatedData } from "./components/CalculatedData";
import { ObjectGrowthCard } from "./components/ObjectGrowthCard";
import { AcquisitionsCard } from "./components/AcquisitionsCard";
import { IncomeAndExpensesCard } from "./components/IncomeAndExpensesCard";

import { DetailedViewProps } from "./DetailedView.types";

import styles from "./DetailedView.module.scss";

const DetailedView = ({
  activePosition,
  post,
  conclusionsPro,
  calculation,
  proCalculationCharts,
  proCalculationDocument,
  onPositionChange,
}: DetailedViewProps) => {
  const { t } = useTranslation();
  
  if (!proCalculationDocument) {
    return (
      <div className={styles.root}>
        <Typography variant="h2">
          {t("No detailed document available")}
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div>
        <TimelineCard {...proCalculationDocument.timeline} />
        <CalculatedData {...proCalculationDocument.calculatedData} />
        <PriceCard
          totalApartments={proCalculationDocument.totalApartments}
          totalParkings={proCalculationDocument.totalParkings}
          {...proCalculationDocument.priceData}
        />
        <AcquisitionsCard {...proCalculationDocument.acquisitionsData} />
        <PenaltyCard {...proCalculationDocument.penaltyData} />
        <FinancingCard {...proCalculationDocument.financingData} />

        <IncomeAndExpensesCard
          totalApartments={proCalculationDocument.totalApartments}
          totalParkings={proCalculationDocument.totalParkings}
          {...proCalculationDocument.incomeAndExpensesData}
        />

        <LoansCard {...proCalculationDocument.loansData} />
        <ObjectGrowthCard {...proCalculationDocument.objectGrowthData} />
        <ClientCard {...proCalculationDocument.clientData} />
        <DetailedTable
          title="Voraussichtliche Einkünfte aus Vermietung und Verpachtung"
          data={proCalculationDocument.tablesData}
          columns={expectedIncomeTableColumns}
          bottomText={expectedIncomeTableBottomText}
        />

        <DetailedTable
          title="voraussichtliche steuerliche Effekte"
          data={proCalculationDocument.tablesData.slice(0, -1)}
          columns={getEstimatedTaxResultsTableColumns()}
          bottomText={estimatedTaxResultsTableBottomText}
        />

        {proCalculationDocument.isKfw297Limit100Enabled ||
          (proCalculationDocument.isKfw297Limit150Enabled && (
            <DetailedTable
              title={proCalculationDocument.specialLoanTitle}
              data={proCalculationDocument.tablesData}
              columns={loanSpecialTableColumns}
            />
          ))}

        <DetailedTable
          title={proCalculationDocument.regularLoanTitle}
          data={proCalculationDocument.tablesData}
          columns={loanRegularTableColumns}
        />

        <DetailedTable
          title="Gesamtübersicht"
          data={proCalculationDocument.tablesData}
          columns={totalOverviewTableColumns}
        />

        <DetailedTable
          title="Gesamtübersicht"
          data={proCalculationDocument.tablesData}
          columns={getSummaryTableColumns({
            isKfw297Limit100Enabled:
              proCalculationDocument.isKfw297Limit100Enabled ?? false,
            isKfw297Limit150Enabled:
              proCalculationDocument.isKfw297Limit150Enabled ?? false,
          })}
        />

        <InfoCard />

        <Typography className={styles.title} variant="h5" weight="semiBold">
          {t("calculator_pro_page.analytics.summary")}
        </Typography>
        <InvestmentBasicData
          withBanner
          post={post}
          activePosition={activePosition}
          onPositionChange={onPositionChange}
          calculation={calculation}
          proCalculationCharts={proCalculationCharts}
          conclusionsPro={conclusionsPro}
        />
      </div>
    </div>
  );
};

export default DetailedView;
